import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getRetailOdDrawdownCreditAccountSummaryByLoanId,
  getTermLoanCreditAccountSummaryByLoanId,
} from "../../actions/loan.action";
import DataDisplaySection from "../../components/datadisplay-section";
import { DatafieldLabel } from "../../components/datafield-section";
import Modal from "../../components/modal";
import { CreditAccountSummaryDetails } from "../../dto";
import { ReactComponent as Loader } from "../../images/loader.svg";
import dispatch from "../../middleware";
import { ReduxState } from "../../reducers";
import { isLoadingActive } from "../../utils";
import { ExtendedLoanType } from "./types";

const BureauDetailsLabel: DatafieldLabel[] = [
  {
    items: [
      { label: "Bureau Score:", key: "creditScore" },
      {
        label: "Max personal loan amount:",
        key: "maxPersonalLoanAmount",
        prefix: "Rs",
        formatMethod: (value: number) => value.toFixed(2),
      },
      {
        label: "Written off/Settled Accounts:",
        key: "writtenOffSettledAccountCount",
      },
      { label: "Age (in years):", key: "userAgeInYears" },
    ],
  },
  {
    items: [
      {
        label: "Bureau Obligation:",
        key: "estimatedObligationAmount",
        prefix: "Rs",
        formatMethod: (value: number) => value.toFixed(2),
      },
      { label: "Number of Overdue Accounts:", key: "overdueAccountCount" },
      {
        label: "Whether NTC:",
        key: "isNewToCredit",
        formatMethod: (isNewToCredit: boolean) =>
          isNewToCredit ? "Yes" : "No",
      },
    ],
  },
];

const EmploymentDetailsLabel: DatafieldLabel[] = [
  {
    items: [
      {
        label: "Employment Duration (in days):",
        key: "employmentDurationInDays",
      },
      // INFO: Commenting this because Number is not matching in User Details
      // {
      //   label: "Employee Monthly Salary:",
      //   key: "employeeMonthlySalary",
      //   prefix: "Rs",
      //   formatMethod: (value: number) => value.toFixed(2),
      // },
    ],
  },
  {
    items: [
      {
        label: "Max Monthly Sanctionable Amount:",
        key: "userMonthlySanctionableAmount",
        prefix: "Rs",
        formatMethod: (value: number) => value.toFixed(2),
      },
      // INFO: Commenting this because Number is not matching in User Details
      // {
      //   label: "Monthly Income:",
      //   key: "income",
      //   prefix: "Rs",
      //   formatMethod: (value: number) => value.toFixed(2),
      // },
    ],
  },
];

const CreditRiskDetailsLabel: DatafieldLabel[] = [
  {
    items: [
      {
        label: "Probability of Default (24 Months):",
        key: "probabilityOfDefault",
        formatMethod: (value: number) => value.toFixed(2),
      },
    ],
  },
  {
    items: [
      {
        label: "Probability of Survival (6 Months):",
        key: "probabilityOfSurvival",
        formatMethod: (value: number) => value.toFixed(2),
      },
    ],
  },
];

const ApprovedDetailsLabel: DatafieldLabel[] = [
  {
    items: [
      { label: "Decision:", key: "", render: () => "APPROVED" },
      {
        label: "Amount:",
        key: "amount",
        prefix: "Rs",
        formatMethod: (value: number) => value.toFixed(2),
      },
    ],
  },
  {
    items: [{ label: "Tenure (in months):", key: "tenureInMonths" }],
  },
];

const getCreditAccountSummaryByLoanType = (
  loanType: ExtendedLoanType,
  loanId: string
) => {
  switch (loanType) {
    case "TERM_LOAN":
      return getTermLoanCreditAccountSummaryByLoanId(loanId);
    case "RETAIL_OD_DRAWDOWN":
      return getRetailOdDrawdownCreditAccountSummaryByLoanId(loanId);
    default:
      return new Error("Invalid loan type");
  }
};

interface CreditAccountSummaryProps {
  loanId: string;
  loanType: Extract<ExtendedLoanType, "TERM_LOAN" | "RETAIL_OD_DRAWDOWN">;
}

export default function CreditAccountSummary({
  loanId,
  loanType,
}: CreditAccountSummaryProps) {
  const storeDispatch = useDispatch();
  const loadingQueue = useSelector(
    (state: ReduxState) => state.loan.loadingQueue
  );
  const isLoading = isLoadingActive(loadingQueue);

  const termLoanCreditAccountSummary: CreditAccountSummaryDetails | null =
    useSelector((state: ReduxState) => state.loan.termLoanCreditAccountSummary);
  const retailOdDrawdownCreditAccountSummary: CreditAccountSummaryDetails | null =
    useSelector(
      (state: ReduxState) => state.loan.retailOdDrawdownLoanCreditAccountSummary
    );

  const creditAccountSummary = useMemo(() => {
    return loanType === "TERM_LOAN"
      ? termLoanCreditAccountSummary
      : retailOdDrawdownCreditAccountSummary;
  }, [
    loanType,
    termLoanCreditAccountSummary,
    retailOdDrawdownCreditAccountSummary,
  ]);

  const [errorModal, setErrorModal] = useState<{
    open: boolean;
    message: string;
  }>({
    open: false,
    message: "",
  });

  const fetchCreditAccountSummary = useCallback(
    async (loanType: ExtendedLoanType, loanId: string) => {
      if (!loanId) return;
      try {
        const action = getCreditAccountSummaryByLoanType(loanType, loanId);
        if (action instanceof Error) throw action;
        await dispatch(storeDispatch, action);
      } catch (error: any) {
        setErrorModal({
          open: true,
          message: error?.message || "An error occurred",
        });
      }
    },
    [storeDispatch]
  );

  useEffect(() => {
    if (!loanId || !loanType) return;
    fetchCreditAccountSummary(loanType, loanId);
  }, [loanId, loanType, fetchCreditAccountSummary]);

  if (isLoading)
    return (
      <div className="d-flex align-items-center justify-content-center">
        <Loader className="text-center mt-4" />
      </div>
    );

  if (!creditAccountSummary)
    return <p className="text-muted text-center">No data available</p>;

  // INFO: This check is for EWA transactions
  if (
    !creditAccountSummary.approvedDetails &&
    !creditAccountSummary.bureauDetails &&
    !creditAccountSummary.creditRiskDetails &&
    !creditAccountSummary.employmentDetails
  ) {
    return (
      <div className="dashboard-container">
        <h3 className="user-page-title mb-4">Credit Assessment Summary</h3>
        <p>
          Credit Assessment Summary is not available for draws made under earned
          wage access product.
        </p>
      </div>
    );
  }

  return (
    <>
      <div>
        <Section
          title="Bureau Parameters"
          data={creditAccountSummary.bureauDetails}
          labels={BureauDetailsLabel}
        />
        <Section
          title="Employment Parameters"
          data={creditAccountSummary.employmentDetails}
          labels={EmploymentDetailsLabel}
        />
        <Section
          title="Risk Model Outcomes Parameters"
          data={creditAccountSummary.creditRiskDetails}
          labels={CreditRiskDetailsLabel}
        />
        <Section
          title="Decision"
          data={creditAccountSummary.approvedDetails}
          labels={ApprovedDetailsLabel}
        />
      </div>
      <Modal
        open={errorModal.open}
        closeOnEscPress
        closeOnBackdropClick
        hasSubmitButton={false}
        title="Not able to load credit account summary"
        description={errorModal.message}
        onClose={() => setErrorModal({ open: false, message: "" })}
        onCancel={() => setErrorModal({ open: false, message: "" })}
      />
    </>
  );
}

interface SectionProps {
  title: string;
  data: any;
  labels: DatafieldLabel[];
}

function Section({ title, data, labels }: SectionProps) {
  return (
    <div className="dashboard-container p-3">
      {data ? (
        <DataDisplaySection
          className="my-3"
          labels={labels}
          data={data}
          withinBox
        />
      ) : (
        <p className="text-muted text-center">No data available</p>
      )}
    </div>
  );
}
